import type { DocNode } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import type { SelectionContext } from '@atlaskit/editor-common/types';
import type { JSONDocNode, JSONNode } from '@atlaskit/editor-json-transformer/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { parseRemixDataConsumerSource } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type {
	PageContentCommandResult,
	ReadContentCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import { log } from '../utils/aifc-logging';

import { CommandHandler } from './command-handler';
import type { PassThroughCommandProps } from './command-handler';

const REMIX_UI_PATH_REGEX = new RegExp('remix\\/ui\\/([^/?#]+)', 'u');

/**
 * Resolves the `media` node from a selection entry, whether the entry is the media node itself or
 * the `mediaSingle` wrapping it. Single source for this traversal — everything below that needs the
 * selected media node, its attrs, or its marks goes through here.
 */
const resolveMediaNode = (node?: JSONNode): JSONNode | undefined => {
	if (!node) {
		return undefined;
	}
	if (node.type === 'media') {
		return node;
	}
	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content[0]) {
		const media = node.content[0] as JSONNode;
		return media?.type === 'media' ? media : undefined;
	}
	return undefined;
};

/** Resolves the selected `media` node from an ADF selection fragment. */
const mediaNodeFromFragment = (
	selectionFragment: JSONNode[] | null | undefined,
): JSONNode | undefined =>
	Array.isArray(selectionFragment) ? resolveMediaNode(selectionFragment[0]) : undefined;

const isMediaNode = (node?: JSONNode) =>
	typeof (resolveMediaNode(node)?.attrs as { id?: string } | undefined)?.id === 'string';

const isMediaNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMediaNode(selection?.selectionFragment?.at(0));
};

/**
 * Extracts the media file ID from the selected ADF fragment when the selection is a media node.
 */
const extractMediaFileId = (selectionFragment: JSONNode[] | null | undefined): string | undefined =>
	(mediaNodeFromFragment(selectionFragment)?.attrs as { id?: string } | undefined)?.id;

/**
 * Extracts the media collection id from the selected ADF fragment when the selection is a media node.
 * Sent to the backend so it can resolve the current media's collection when editing an existing image.
 */
const extractMediaCollection = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined =>
	(mediaNodeFromFragment(selectionFragment)?.attrs as { collection?: string } | undefined)
		?.collection;

/**
 * Extracts the remix `dataConsumer` mark source from the selected media node.
 *
 * Written at insert time by `getRemixDataConsumerSource`. Unlike the in-memory remix history this
 * lives in the saved document, so it is the only subtype source that survives a reload — which is
 * the case this fallback exists for (MAUI-1185).
 */
const extractRemixDataConsumerSource = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	const marks = mediaNodeFromFragment(selectionFragment)?.marks as
		| Array<{ attrs?: { sources?: unknown }; type?: string }>
		| undefined;
	const sources = marks?.find((mark) => mark?.type === 'dataConsumer')?.attrs?.sources;
	return Array.isArray(sources) && typeof sources[0] === 'string' ? sources[0] : undefined;
};

const isMauiAppNode = (node?: JSONNode) => {
	return (
		node?.type === 'extension' &&
		(node.attrs as { extensionKey?: string })?.extensionKey === 'native-embed:maui'
	);
};

const isMauiAppNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMauiAppNode(selection?.selectionFragment?.at(0));
};

const isADFEntity = (value: ReadContentCommandProperties['prompt']): value is ADFEntity =>
	typeof value === 'object' &&
	value !== null &&
	!Array.isArray(value) &&
	typeof (value as { type?: unknown }).type === 'string';

// Safe here means "does not introduce external/contextual references that the
// editor-minion fast path cannot resolve." Formatting marks are allowed; link
// marks are checked separately because they can carry external context.
const SAFE_PROMPT_NODE_TYPES = new Set([
	'doc',
	'paragraph',
	'text',
	'emoji',
	'hardBreak',
	'date',
	'status',
	'placeholder',
]);

// Unknown nodes are treated as rich by omission from SAFE_PROMPT_NODE_TYPES.
const isSafePromptNode = (node: ADFEntity): boolean =>
	SAFE_PROMPT_NODE_TYPES.has(node.type) && !node.marks?.some((mark) => mark.type === 'link');

const hasRichPromptContent = (prompt: ADFEntity): boolean => {
	let hasRichContent = false;
	traverse(prompt, {
		any: (node) => {
			if (!isSafePromptNode(node)) {
				hasRichContent = true;
			}
		},
	});

	return hasRichContent;
};

const isPlainPrompt = (prompt?: ReadContentCommandProperties['prompt']): boolean =>
	isADFEntity(prompt) && prompt.type === 'doc' && !hasRichPromptContent(prompt);

/**
 * Extracts the Maui content ID from the selected ADF fragment.
 */
const extractMauiContentId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	const mauiApp = selectionFragment.find((node) => isMauiAppNode(node));
	const attrs = mauiApp?.attrs as
		| { extensionKey?: string; parameters?: { macroParams?: { url?: { value?: string } } } }
		| undefined;

	const url: string | undefined = attrs?.parameters?.macroParams?.url?.value;
	if (url) {
		// We only want the ID, which is the part after /remix/ui/
		const match = REMIX_UI_PATH_REGEX.exec(url);
		return match?.[1];
	}
	return undefined;
};

export const buildContentContext = async ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
	contentMauiId,
	dynamicUiSource,
	dynamicUiType,
	dynamicUiSubtype,
	mediaFileId,
	mediaCollection,
	startIndex,
	endIndex,
	isEditorInlineRovoActive,
	aiExperienceProduct,
	source,
	prompt,
}: {
	aiExperienceProduct?: string;
	contentMauiId?: string;
	doc: DocNode | JSONDocNode | undefined;
	dynamicUiSource?: PageContentCommandResult['dynamicUiSource'];
	dynamicUiSubtype?: string;
	dynamicUiType?: string;
	endIndex?: number;
	isEditorInlineRovoActive?: boolean;
	mediaCollection?: string;
	mediaFileId?: string;
	prompt?: ReadContentCommandProperties['prompt'];
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
	source?: string;
	startIndex?: number;
}): Promise<PageContentCommandResult> => {
	let additionalContextFromProps = props.additionalContext || { isViewMode: false };
	if (typeof additionalContextFromProps === 'function') {
		try {
			additionalContextFromProps = await additionalContextFromProps();
			// eslint-disable-next-line no-unused-vars
		} catch (err) {
			additionalContextFromProps = {};
		}
	}
	const {
		isViewMode = false,
		isDraftLockedForEditing = false,
		...additionalContext
	} = additionalContextFromProps || {};

	// Prefer an explicit `hasRealTimeCollab` prop when provided. Deriving it from
	// `editorAPI.collabEdit` races the collab plugin's initialization, so a freshly
	// mounted editor can report `false` and be wrongly routed to frontend streaming.
	// The derived value is only used when the prop is nullish, so an explicit
	// `false` is respected.
	const hasRealTimeCollab = props.hasRealTimeCollab ?? !!props.editorAPI?.collabEdit;

	// HTML+ v2 incremental tool-call streaming capability advertisement.
	// Per FE spec §2.5 + §3.8, FE sends a single int `editorStreamingContractVersion`
	// on the editor JSON block. Backend reads `>= 2` to opt the request into
	// the v2 wire format. Gated by `platform_editor_use_html_plus_streaming_parser`
	// — when the gate is off the field is omitted entirely so v1 wire bytes
	// stay byte-identical to today.
	const editorStreamingContractVersion = expValEquals(
		'platform_editor_use_html_plus_streaming_parser',
		'isEnabled',
		true,
	)
		? 2
		: undefined;

	// When true, the backend will skip the orchestrator and call the editor minion directly.
	const useFastPath =
		isEditorInlineRovoActive &&
		aiExperienceProduct === 'CONFLUENCE' &&
		source === 'CONFLUENCE_INLINE_EDITOR' &&
		isPlainPrompt(prompt) &&
		isExperimentEnabled('platform_editor_ai_minion_skip_orchestrator')
			? true
			: undefined;

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		contentMauiId,
		dynamicUiSource,
		dynamicUiType,
		dynamicUiSubtype,
		mediaFileId,
		mediaCollection,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		startIndex,
		endIndex,
		isViewMode,
		isDraftLockedForEditing,
		hasRealTimeCollab,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
		...(editorStreamingContractVersion !== undefined ? { editorStreamingContractVersion } : {}),
		...(useFastPath !== undefined ? { useFastPath } : {}),
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	ReadContentCommandProperties,
	PageContentCommandResult | null
> {
	async execute(payload?: ReadContentCommandProperties): Promise<PageContentCommandResult | null> {
		const defaultResult: PageContentCommandResult = await buildContentContext({
			props: this.props,
			doc: undefined,
			selectionFragment: '',
			selectionLocalIds: '',
			selectionMarkdown: '',
		});

		if (
			expValEquals('cc-markdown-mode', 'isEnabled', true) &&
			this.props.editorAPI?.markdownMode?.sharedState.currentState()?.isMarkdownMode
		) {
			return null;
		}

		try {
			if (isExperimentEnabled('platform_editor_local_id_reliability')) {
				this.props.editorAPI.core.actions.execute(
					this.props.editorAPI.localId?.commands.addMissingLocalIds({
						onRepaired: ({ repairedNodeCount }) => {
							this.props.editorAPI.analytics?.actions.fireAnalyticsEvent({
								action: ACTION.LOCAL_IDS_REPAIRED,
								actionSubject: ACTION_SUBJECT.AI_STREAMING,
								actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
								attributes: { repairedNodeCount },
								eventType: EVENT_TYPE.OPERATIONAL,
							});
						},
					}),
				);
			}

			const doc = await this.props.editorAPI.core.actions.requestDocumentAsync();
			if (!doc) {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}

			const aiExperiencePluginState =
				this.props.editorAPI.aiExperience?.sharedState?.currentState()
					?.aiDecorationExperiencePluginState;
			const isEditorInlineRovoActive = aiExperiencePluginState?.isRunning;
			const aiExperienceProduct = aiExperiencePluginState?.product;
			const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
			const selectionFragment = selection?.selectionFragment
				? JSON.stringify(selection.selectionFragment)
				: '';
			const selectedMediaFileId = isMediaNodeSelected(selection)
				? extractMediaFileId(selection?.selectionFragment)
				: undefined;
			const selectedMediaDynamicUiSubtypeFromHistory =
				selectedMediaFileId && isExperimentEnabled('cc_maui_polish_changes_batch_5')
					? this.props.editorAPI.malleableUi?.actions.getRemixDynamicUiSubtype(selectedMediaFileId)
					: undefined;
			// Remix history is in-memory and keyed by conversationId, so it is empty once the user has
			// reloaded or navigated away and come back. Fall back to the durable `dataConsumer` mark on
			// the media node in that case.
			//
			// History is preferred when present because it is the established in-session source and a
			// cheap in-memory lookup, where reading the mark means walking the selection ADF. The two
			// agree in practice: a type switch re-inserts the media node with a freshly written mark,
			// so the mark does not go stale behind history.
			//
			// Note the `??` also means this fallback covers in-session edits whenever
			// `cc_maui_polish_changes_batch_5` is off, since the history branch then yields nothing.
			const selectedMediaDynamicUiSubtype =
				selectedMediaDynamicUiSubtypeFromHistory ??
				(selectedMediaFileId && isExperimentEnabled('cc_maui_polish_changes_batch_7')
					? parseRemixDataConsumerSource(
							extractRemixDataConsumerSource(selection?.selectionFragment),
						)?.subtype
					: undefined);
			const content: PageContentCommandResult = await buildContentContext({
				props: this.props,
				doc,
				selectionFragment,
				startIndex: selection?.startIndex ?? undefined,
				endIndex: selection?.endIndex ?? undefined,
				selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
				selectionMarkdown: selection?.selectionMarkdown ?? '',
				isEditorInlineRovoActive,
				aiExperienceProduct,
				source: payload?.source,
				prompt: payload?.prompt,
				...(expValEquals('cc-maui-experiment', 'isEnabled', true)
					? {
							...(isMauiAppNodeSelected(selection)
								? { contentMauiId: extractMauiContentId(selection?.selectionFragment) }
								: {}),
							...(selectedMediaFileId
								? {
										mediaFileId: selectedMediaFileId,
										...(fg('platform_editor_maui_remix_media_collection')
											? { mediaCollection: extractMediaCollection(selection?.selectionFragment) }
											: {}),
										dynamicUiType: 'infographic',
										...(selectedMediaDynamicUiSubtype
											? { dynamicUiSubtype: selectedMediaDynamicUiSubtype }
											: {}),
									}
								: {}),
							dynamicUiSource: {
								contentType: 'page',
								startLocalId: selection?.localIds?.[0],
								endLocalId: selection?.localIds?.[selection.localIds.length - 1],
								startIndex: selection?.startIndex ?? undefined,
								endIndex: selection?.endIndex ?? undefined,
								fragmentAdf: selectionFragment,
							},
						}
					: {}),
			});

			log('handle read-content-command result', {
				additionalContext: this.props.additionalContext,
				doc,
				selection,
				content,
			});

			return content ?? defaultResult;
		} catch {
			// TODO: EDITOR-123 - report this error
			return defaultResult;
		}
	}
}
